import { Fragment, type ReactNode } from "react";

/**
 * The four things a Complao message may be formatted with, rendered — and nothing else.
 *
 * The transcript used to render every body as plain text inside a `white-space: pre-wrap`
 * bubble. The model writes for the auditee, and the prompt asks it for a document name in
 * bold and sub-parts as a list, so the client read `**DOC-001 Quality Manual**` with the
 * asterisks in it. Either the asterisks go or the formatting does; the formatting is what
 * makes a three-part question answerable, so the asterisks go.
 *
 * **A subset, deliberately, and it is the same subset the system prompt names.** Blank-line
 * paragraphs, `- ` bullets, `**bold**`, and a single line break kept as a line break.
 * Headings, tables, rules, links and code fences are not rendered and not asked for: a
 * heading inside a two-paragraph chat message is furniture, and a link is a place to put
 * something that does not belong in an audit record.
 *
 * ⚠ **No `dangerouslySetInnerHTML`, at any point.** This returns React elements, so the
 * text is escaped by React itself. That matters more here than in most places: the same
 * component renders what the *client* typed, and a transcript is read afterwards by the
 * certification body and by an assessor. Markup arriving in a client's answer is displayed,
 * never executed.
 *
 * Anything unmatched falls through as literal text. A model that emits a table gets a table
 * shown as the characters it wrote — ugly, honest, and reported rather than swallowed.
 */
export function RichText({ text }: { text: string }): ReactNode {
  return blocks(text).map((block, index) =>
    block.kind === "list" ? (
      <ul className="rt-list" key={index}>
        {block.items.map((item, position) => (
          <li key={position}>{inline(item)}</li>
        ))}
      </ul>
    ) : (
      <p className="rt-p" key={index}>
        {inline(block.text)}
      </p>
    ),
  );
}

type Block = { kind: "para"; text: string } | { kind: "list"; items: string[] };

const BULLET = /^\s*[-•*]\s+/;

/**
 * Paragraphs and lists, in the order they were written.
 *
 * A run of bullet lines is one list however it is separated, because a model that puts a
 * blank line between two bullets means one list and not two — and two `<ul>`s in a row show
 * a gap the writer did not ask for.
 */
function blocks(text: string): Block[] {
  const out: Block[] = [];
  let paragraph: string[] = [];

  const flush = () => {
    const joined = paragraph.join("\n").trim();
    if (joined) out.push({ kind: "para", text: joined });
    paragraph = [];
  };

  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (BULLET.test(line)) {
      flush();
      const item = line.replace(BULLET, "");
      const last = out[out.length - 1];
      if (last?.kind === "list") last.items.push(item);
      else out.push({ kind: "list", items: [item] });
      continue;
    }
    if (!line.trim()) {
      flush();
      continue;
    }
    paragraph.push(line);
  }
  flush();
  return out;
}

// Paired `**` only. A single asterisk stays an asterisk — "5 * 3" is arithmetic, and a
// client who types one is not asking for emphasis.
const BOLD = /\*\*([^*]+)\*\*/g;

/** `**bold**` and single line breaks. Everything else is the characters that were written. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;

  const push = (value: string, bold: boolean) => {
    if (!value) return;
    // A line break inside a paragraph is kept: the prompt asks for the question on its own
    // line under the requirement it cites, and collapsing that puts them in one run-on.
    const parts = value.split("\n");
    const rendered = parts.map((part, index) => (
      <Fragment key={index}>
        {index > 0 ? <br /> : null}
        {part}
      </Fragment>
    ));
    out.push(
      bold ? (
        <strong key={key++}>{rendered}</strong>
      ) : (
        <Fragment key={key++}>{rendered}</Fragment>
      ),
    );
  };

  BOLD.lastIndex = 0;
  for (let match = BOLD.exec(text); match; match = BOLD.exec(text)) {
    push(text.slice(cursor, match.index), false);
    push(match[1] ?? "", true);
    cursor = match.index + match[0].length;
  }
  push(text.slice(cursor), false);
  return out;
}

/**
 * The same message, as words to be spoken — M31.
 *
 * It lives here rather than in `readout.ts` because it is the *same* subset: what the
 * renderer turns into elements this turns into speech, and a fifth thing added to one
 * without the other would be read aloud as its own characters. A synthesiser handed
 * `**DOC-001 Quality Manual**` says "asterisk asterisk" twice, which is the defect
 * `RichText` was built to remove, arriving through the speaker instead of the screen.
 *
 * Three things happen and nothing else:
 *
 * * **`**bold**` loses its asterisks and keeps its words.** Emphasis is not re-voiced —
 *   the synthesiser is not asked to stress a document name, because a machine leaning on
 *   a word in an audit question changes what the question asks.
 * * **A bullet without terminal punctuation gets a full stop.** Only in what is spoken:
 *   the stored message is untouched, and this is prosody, not editing. Without it the
 *   synthesiser runs two sub-parts of a three-part question into one sentence, which is
 *   exactly the reading the list exists to prevent.
 * * **Paragraphs and lists are separated by a line break**, which every engine reads as
 *   a pause.
 */
export function spokenText(text: string): string {
  return blocks(text)
    .map((block) =>
      block.kind === "list"
        ? block.items.map((item) => stopped(unbold(item))).join("\n")
        : unbold(block.text),
    )
    .join("\n")
    .trim();
}

/** Paired `**` only — the same pairing `inline` renders, so the two cannot disagree. */
function unbold(text: string): string {
  return text.replace(BOLD, "$1");
}

function stopped(item: string): string {
  return /[.!?:;,]$/.test(item.trim()) ? item : `${item.trimEnd()}.`;
}
